import { createElement } from 'react';
import {
  TextFields as ContainsIcon,
  SearchOff as NotContainsIcon,
  FormatAlignLeft as StartsWithIcon,
  FormatAlignRight as EndsWithIcon,
  DragHandle as EqualsIcon,
  NotInterested as NotEqualsIcon,
  ChevronLeft as LessThanIcon,
  ChevronRight as GreaterThanIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import type { BoxProps } from '@mui/material';
import type { ColumnMetadata } from '../../../types';
import type { ColumnType } from '../types';
import { GridDateTimeMode, parseBackendDateTime } from '../../../utils/dateFormatters';
import type { GridDateTimeMode as GridDateTimeModeType } from '../../../utils/dateFormatters';
import { SUPPORTED_TOTAL_SUMMARIES, SUPPORTED_GROUP_SUMMARIES } from './summaryConstants';
import {
  extractAppMediaPath,
  IMAGE_FILE_EXTENSIONS,
} from '../../../../detail-form/richTextMediaHelpers';

type SizedColumn = { getSize: () => number; columnDef: { meta?: unknown } };

export const getColumnWidth = (column: SizedColumn): string => {
  const width = (column.columnDef.meta as { width?: string } | undefined)?.width;
  return width || `${column.getSize()}px`;
};

/**
 * Detect column type from metadata
 */
export const getColumnType = (metadata?: ColumnMetadata): ColumnType => {
  if (!metadata) return 'text';

  const type = metadata.type || metadata.dataType;

  if (type === 'boolean') return 'boolean';
  if (type === 'date') return 'date';
  if (type === 'datetime') return 'datetime';
  if (type === 'number' || type === 'int' || type === 'decimal' || type === 'float')
    return 'number';

  return 'text';
};

/**
 * Check if a column is a date type
 */
export const isDateColumn = (metadata?: ColumnMetadata): boolean => {
  if (!metadata) return false;
  const type = metadata.type || metadata.dataType;
  return type === 'date' || type === 'datetime';
};

/**
 * Check if a column is a number type
 */
export const isNumberColumn = (metadata?: ColumnMetadata): boolean => {
  if (!metadata) return false;
  const type = metadata.type || metadata.dataType;
  return type === 'number' || type === 'int' || type === 'decimal' || type === 'float';
};

/**
 * Check if a column is a boolean type
 */
export const isBooleanColumn = (metadata?: ColumnMetadata): boolean => {
  if (!metadata) return false;
  const type = metadata.type || metadata.dataType;
  return type === 'boolean';
};

/**
 * Check if a column contains a JSON-serialized list
 */
export const isListColumn = (metadata?: ColumnMetadata): boolean => {
  if (!metadata) return false;
  const candidates = [metadata.type, metadata.dataType, metadata.DataType as string];
  return candidates.some((type) => typeof type === 'string' && type.toLowerCase() === 'list');
};

/**
 * Check if a column is an image type
 */
export const isImageColumn = (columnMeta?: ColumnMetadata): boolean => {
  if (!columnMeta) return false;
  const candidates = [columnMeta.type, columnMeta.dataType, columnMeta.DataType as string];
  return candidates.some((c) => typeof c === 'string' && c.toLowerCase() === 'image');
};

export const isScreenshotColumn = (columnMeta?: ColumnMetadata): boolean => {
  if (!isImageColumn(columnMeta)) return false;
  const fieldName = columnMeta?.dataField || columnMeta?.field;
  return fieldName === 'ScreenshotPath' || fieldName === 'PathToScreenshot';
};

const extractAppMediaImagesFromHtml = (value: string): string[] => {
  if (!/<img\b/i.test(value) || typeof DOMParser === 'undefined') return [];

  const document = new DOMParser().parseFromString(value, 'text/html');
  return Array.from(document.querySelectorAll('img')).reduce<string[]>((paths, image) => {
    const imagePath = extractAppMediaPath(image.getAttribute('src') ?? '');
    if (imagePath && !paths.includes(imagePath)) paths.push(imagePath);
    return paths;
  }, []);
};

/**
 * Removes AppMedia image elements while preserving any surrounding text and
 * formatting from an HTML-valued grid cell.
 */
export const getAppMediaImageTextHtml = (value: unknown): string => {
  if (typeof value !== 'string' || !/<img\b/i.test(value) || typeof DOMParser === 'undefined') {
    return '';
  }

  const document = new DOMParser().parseFromString(value, 'text/html');
  document.querySelectorAll('img').forEach((image) => image.remove());
  return document.body.innerHTML;
};

/**
 * Returns an AppMedia image path without treating ordinary HTML or text as an
 * image. Image metadata permits paths without a conventional file extension;
 * string columns require an actual img element or an image-looking path.
 */
export const getAppMediaImagePath = (
  value: unknown,
  columnMeta?: ColumnMetadata
): string | null => {
  return getAppMediaImagePaths(value, columnMeta)[0] ?? null;
};

export const getAppMediaImagePaths = (value: unknown, columnMeta?: ColumnMetadata): string[] => {
  if (typeof value !== 'string' || !value.trim()) return [];
  if (!value.includes('/AppMedia/')) return [];

  const htmlImagePaths = extractAppMediaImagesFromHtml(value);
  if (htmlImagePaths.length) return htmlImagePaths;

  const path = extractAppMediaPath(value);
  if (!path) return [];

  const isConfiguredImage = isImageColumn(columnMeta);
  return isConfiguredImage || IMAGE_FILE_EXTENSIONS.test(path) ? [path] : [];
};

/**
 * Check if a column is a file type (e.g., resume/CV uploads).
 * Backend returns DataType: "file" explicitly for these fields.
 */
export const isFileColumn = (columnMeta?: ColumnMetadata): boolean => {
  if (!columnMeta) return false;
  const candidates = [columnMeta.type, columnMeta.dataType, columnMeta.DataType as string];
  return candidates.some((c) => typeof c === 'string' && c.toLowerCase() === 'file');
};

/** Check whether a column stores a CSS color value. */
export const isColorColumn = (columnMeta?: ColumnMetadata): boolean => {
  if (!columnMeta) return false;
  const candidates = [columnMeta.type, columnMeta.dataType, columnMeta.editor];
  return candidates.some(
    (candidate) =>
      typeof candidate === 'string' &&
      (candidate.toLowerCase() === 'color' || candidate.toLowerCase() === 'dxcolorbox')
  );
};

/**
 * Check if a column has a recognized total summary configured (backend field
 * `totalSummaries`). Drives whether the floating summary footer row renders
 * at all, and whether a per-column footer cell is shown for this column.
 *
 * The set of supported types is defined in `summaryConstants.ts`.
 */
export const isSummaryColumn = (columnMeta?: ColumnMetadata): boolean => {
  if (!columnMeta) return false;
  const raw = (columnMeta as Record<string, unknown>).totalSummaries;
  return typeof raw === 'string' && SUPPORTED_TOTAL_SUMMARIES.has(raw.toLowerCase());
};

/**
 * Check if a column is a lookup / selectgrid column
 */
export const isLookupColumn = (columnMeta?: ColumnMetadata, lookupMap?: unknown): boolean => {
  if (!columnMeta) return false;
  return columnMeta.editor === 'selectgrid' || Boolean(lookupMap);
};

const parseSummaryTypes = (value: string | null | undefined): string[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === 'string');
    }
  } catch {
    // Older BO metadata can contain the summary type as a plain string.
  }

  return [value];
};

/** Returns the supported group aggregation configured in BO, if any. */
export const getGroupSummaryType = (columnMeta?: ColumnMetadata): string | undefined => {
  const summaryType = columnMeta?.groupSummaries?.summaryType;
  return parseSummaryTypes(summaryType).find((type) =>
    SUPPORTED_GROUP_SUMMARIES.has(type.toLowerCase())
  );
};

export const isGroupSummaryColumn = (columnMeta?: ColumnMetadata): boolean =>
  getGroupSummaryType(columnMeta) !== undefined;

/**
 * Format date value for display
 */
export const formatDateValue = (
  value: unknown,
  dateTimeMode?: GridDateTimeModeType,
  includeTime = true
): string => {
  if (value === null || value === undefined) return '-';

  try {
    const date = dateTimeMode ? parseBackendDateTime(value) : new Date(String(value));
    if (!date) return String(value);
    if (isNaN(date.getTime())) {
      return String(value);
    }

    const useUtc = dateTimeMode === GridDateTimeMode.Utc;
    const day = String(useUtc ? date.getUTCDate() : date.getDate()).padStart(2, '0');
    const month = String((useUtc ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, '0');
    const year = useUtc ? date.getUTCFullYear() : date.getFullYear();
    const hours = String(useUtc ? date.getUTCHours() : date.getHours()).padStart(2, '0');
    const minutes = String(useUtc ? date.getUTCMinutes() : date.getMinutes()).padStart(2, '0');

    return includeTime ? `${day}/${month}/${year}, ${hours}:${minutes}` : `${day}/${month}/${year}`;
  } catch {
    return String(value);
  }
};

const operatorIcons: Record<string, ReturnType<typeof createElement>> = {
  contains: createElement(ContainsIcon, { fontSize: 'small' }),
  notcontains: createElement(NotContainsIcon, { fontSize: 'small' }),
  startswith: createElement(StartsWithIcon, { fontSize: 'small' }),
  endswith: createElement(EndsWithIcon, { fontSize: 'small' }),
  '=': createElement(EqualsIcon, { fontSize: 'small' }),
  '<>': createElement(NotEqualsIcon, { fontSize: 'small' }),
  '<': createElement(LessThanIcon, { fontSize: 'small' }),
  '>': createElement(GreaterThanIcon, { fontSize: 'small' }),
  '<=': createElement(
    Box,
    { component: 'span', sx: { fontSize: '1.1rem', fontWeight: 'bold' } } as BoxProps,
    '≤'
  ),
  '>=': createElement(
    Box,
    { component: 'span', sx: { fontSize: '1.1rem', fontWeight: 'bold' } } as BoxProps,
    '≥'
  ),
  between: createElement(Box, { component: 'span', sx: { fontSize: 'small' } } as BoxProps, '⟷'),
};

const fallbackIcon = createElement(FilterListIcon, { fontSize: 'small' });

export const getOperatorIcon = (operator?: string) =>
  (operator && operatorIcons[operator]) ?? fallbackIcon;

const operatorPlaceholders: Record<string, string> = {
  contains: 'Contains...',
  notcontains: 'Does not contain...',
  startswith: 'Starts with...',
  endswith: 'Ends with...',
  '=': 'Equals...',
  '<>': 'Does not equal...',
  '<': 'Less than...',
  '>': 'Greater than...',
  '<=': 'Less than or equal to...',
  '>=': 'Greater than or equal to...',
  between: 'Between...',
};

export const getOperatorPlaceholder = (operator?: string): string =>
  (operator && operatorPlaceholders[operator]) ?? 'Search...';

/** Selection / actions columns — no per-column filter or global search. */
export const isUtilityGridColumn = (columnId: string): boolean =>
  columnId === '_selection' ||
  columnId === '_actions' ||
  columnId === 'actions' ||
  columnId === '_navigate' ||
  columnId === '_deleteScreenshot';

/**
 * Strips HTML tags and collapses whitespace to produce a plain-text string
 * suitable for tooltip display or accessible labels.
 */
export function stripHtmlToPlainText(html: string): string {
  return html
    .replace(/<\/?(br|p|div)[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
